/**
 * Help content for each screen, keyed by nav leaf name.
 * Each entry has a title, intro, and steps array.
 */
const helpContent = {
  upload: {
    title: "Upload",
    intro: "Upload your data files to begin the analysis pipeline.",
    steps: [
      "Drag and drop a ZIP archive containing your data files onto the upload area.",
      "The system will extract the archive, convert Excel files to CSV, and detect column headers automatically.",
      "Once the upload completes, navigate to the Files tab to review what was extracted.",
    ],
    tips: [
      "Supported formats inside the ZIP: .xlsx, .xls, and .csv files.",
      "Large files may take a moment to process — watch the progress modal for status.",
    ],
  },
  files: {
    title: "Files",
    intro: "Review uploaded files and see how they've been grouped by column structure.",
    steps: [
      "The left column shows the directory tree of all uploaded and extracted files.",
      "The right column shows files grouped by matching column headers — files with identical columns are grouped together.",
      "Groups are used downstream in Data Groups to map columns to canonical names.",
    ],
    tips: [
      "If files appear ungrouped, they may have different column structures and need to be reviewed.",
    ],
  },
  canonicals: {
    title: "Column Mapping",
    intro: "Map the columns in your uploaded data to the standard field names used by the analysis engine.",
    steps: [
      "Select a data group from the left panel.",
      "For each canonical field (e.g., Issue Date, Amount, Buyer Name), select the matching column from your data using the dropdown.",
      "Required fields are marked — all mandatory fields must be mapped before you can build the master frame.",
      "Use the sample data preview to verify your mappings look correct.",
    ],
    tips: [
      "The system remembers column mappings, so if you re-upload similar data the mappings may auto-populate.",
      "Column names are matched case-insensitively when suggesting mappings.",
    ],
  },
  components: {
    title: "Components",
    intro: "Build the master data frame by merging your mapped data groups.",
    steps: [
      "Review the data groups that will be included in the merge.",
      "Click Build to combine all included groups into a single master dataset.",
      "The build process applies your column mappings and concatenates all rows.",
    ],
    tips: [
      "After building, navigate to Filters to exclude unwanted records, or to Master Data to preview the result.",
    ],
  },
  filters: {
    title: "Filters",
    intro: "Apply filters to exclude unwanted records from the master dataset before analysis.",
    steps: [
      "Select a column to filter on from the dropdown.",
      "Choose a filter type: binary (include/exclude specific values) or regex (pattern matching).",
      "Configure the filter criteria and apply.",
      "The filtered dataset is saved separately — the raw master is preserved.",
    ],
    tips: [
      "Filters are applied in order. You can add multiple filters to progressively narrow the dataset.",
      "Check the row count before and after filtering to verify the expected number of records remain.",
    ],
  },
  master: {
    title: "Master Data",
    intro: "Preview the filtered master dataset that will be used for all downstream analyses.",
    steps: [
      "Browse the first 100 rows of the filtered dataset.",
      "Verify that column types, dates, and amounts look correct.",
      "If something looks wrong, go back to Column Mapping or Filters to adjust.",
    ],
  },
  documenttypes: {
    title: "Document Types",
    intro: "Classify each document type into rollforward activity categories (Sales, Collection, Dilution, etc.).",
    steps: [
      "Each document type has two pills: positive (+) for debit amounts and negative (-) for credit amounts.",
      "Drag each pill into the appropriate category pad below (Sales, Collection, Dilution, DMR Collection, DMR Dilution).",
      "Use the Amount by Type table on the right to understand the volume of each document type.",
      "Save your mappings as a template for reuse on future deals using the Templates button.",
    ],
    tips: [
      "This mapping is critical for the Rollforward calculation — every document type must be classified.",
      "Load a saved template to quickly apply mappings from a previous deal.",
    ],
  },
  buyerratings: {
    title: "Buyer Ratings",
    intro: "Assign credit ratings to each buyer parent for use in concentration limit calculations.",
    steps: [
      "Buyers are listed in descending order by total accounts receivable.",
      "Drag a rating pill (AA, A, BBB, NIG/NR) onto a buyer row to assign it.",
      "Use the AI classify button to get a suggested rating based on public data.",
      "Load a saved template to bulk-apply ratings from a previous deal.",
    ],
    tips: [
      "Unassigned buyers default to the lowest rating tier in your rating scale.",
      "You can customize the rating tiers and their concentration limit percentages in the Rating Scale section under Parameters.",
    ],
  },
  parameters: {
    title: "Parameters & Launch",
    intro: "Configure analysis parameters and launch the full calculation pipeline.",
    steps: [
      "Set the reference date — this determines the point-in-time snapshot for all balance calculations.",
      "Review and adjust eligibility thresholds, performance ratios, and advance rate parameters.",
      "When ready, click Calculate to run all analyses in sequence.",
    ],
    tips: [
      "The reference date defaults to the latest date in your dataset.",
      "Calculations may take a few minutes depending on the size of the deal.",
    ],
  },
  aging: {
    title: "Aging",
    intro: "View the aging time series showing how receivable balances age over time across overdue buckets.",
    steps: [
      "Each row represents a reference date, with columns showing balances in each aging bucket (Current, 1-30, 31-60, 61-90, 90+).",
      "Scroll through the time series to identify trends in overdue balances.",
      "Use this data to assess the health of the receivable pool over time.",
    ],
  },
  rollforward: {
    title: "Rollforward",
    intro: "Daily activity reconciliation showing how the receivable balance changes through Sales, Collections, and Dilutions.",
    steps: [
      "Each row is a day, showing the beginning balance, activity by category, and ending balance.",
      "The Discrepancy column flags any days where the ending balance doesn't reconcile.",
      "Use this to verify data integrity and understand daily cash flow patterns.",
    ],
    tips: [
      "Large discrepancies may indicate missing document types in the Document Types mapping.",
    ],
  },
  buyerparent: {
    title: "Accounts Receivable",
    intro: "Detailed breakdown of receivables by buyer parent, including overdue analysis and default metrics.",
    steps: [
      "Set a reference date to compute the point-in-time snapshot.",
      "Click Calculate to generate the buyer-level AR analysis.",
      "Review total debit, credit, net balance, and percentage over 90 days past due for each buyer.",
    ],
  },
  concentrationlimits: {
    title: "Concentration Limits",
    intro: "Calculate the maximum allowable exposure per buyer based on their credit rating.",
    steps: [
      "Set a reference date and click Calculate.",
      "Each buyer's concentration limit is determined by their rating tier's percentage applied to total eligible receivables.",
      "Excess concentration is flagged for buyers exceeding their limit.",
    ],
  },
  nerb: {
    title: "NERB",
    intro: "Net Eligible Receivable Balance — the final eligible pool after applying all adjustments and concentration limits.",
    steps: [
      "Click Calculate to compute the NERB time series.",
      "Review how the eligible balance changes over time as buyers move in and out of concentration.",
    ],
  },
  delinquencies: {
    title: "Delinquencies",
    intro: "Analyse delinquency rates and trends across the receivable pool.",
    steps: [
      "Click Calculate to generate delinquency metrics.",
      "Review the delinquency ratios and identify any concerning trends.",
    ],
  },
  dilution: {
    title: "Dilution",
    intro: "Measure dilution rates — the proportion of receivables reduced by non-cash adjustments (credit notes, discounts, etc.).",
    steps: [
      "Click Calculate to generate dilution metrics.",
      "Review dilution ratios and trends to assess the stability of the receivable pool.",
    ],
  },
  dashboard: {
    title: "Dashboard",
    intro: "High-level overview of all key metrics and analysis results for the deal.",
    steps: [
      "Review summary KPIs at the top: total AR, eligible balance, advance rate, dilution, and delinquency ratios.",
      "Navigate to specific analyses using the tabs for deeper investigation.",
    ],
  },
  report: {
    title: "Report",
    intro: "Generate an AI-powered executive report summarizing the deal analysis.",
    steps: [
      "Click Generate Report to create a narrative summary using all available analysis data.",
      "The report includes key findings, risk factors, and recommendations.",
      "Review and edit the generated report before downloading.",
    ],
    tips: [
      "The report uses all completed analyses — run as many as possible before generating for a comprehensive output.",
    ],
  },
  downloads: {
    title: "Downloads",
    intro: "Download analysis outputs in various formats.",
    steps: [
      "Master Data section: download the raw or filtered master as CSV, Excel, or Parquet.",
      "Analysis section: download individual analysis outputs in your preferred format.",
      "Bulk Export: download all analyses as a combined Excel workbook.",
      "Report: download the executive report as a PowerPoint presentation.",
    ],
  },
};

export default helpContent;
